import { ZPlayerUpdate } from '@daymade/api-contracts/player/models';
import GenericModal from 'components/GenericModal';
import ImageWithFallback from 'components/ImageWithFallback';
import RoundedButton from 'components/RoundedButton';
import { useUIStore } from 'hooks/stores/useUIStore';
import { useUserStore } from 'hooks/stores/useUserStore';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { playerApiClient, subscriptionApiClient } from 'utils/apiClient';
import HttpStatusCode from 'utils/HttpStatusCode';
import { validEmail } from 'utils/RegistrationValidator';
import styles from './ChangeEmailPopup.module.css';
import { tryApi } from 'utils/api/common';

interface IChangeEmailPopup {
  currentEmailAddress?: string;
  subscriptionId?: string;
  onClose: () => void;
}
const ChangeEmailPopup = observer(
  ({ currentEmailAddress, subscriptionId, onClose }: IChangeEmailPopup) => {
    const { displayToast } = useUIStore();
    const { currentUser, updateCurrentUser } = useUserStore();

    const [newEmailAddressText, setNewEmailAddressText] = useState<string>('');
    const [ctaLoading, setCtaLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    if (!currentEmailAddress) return null;

    return (
      <GenericModal onClose={onClose} noTextStyleOverrides>
        <div className={styles.changeEmailContainer}>
          <div className={styles.imageContainer}>
            <ImageWithFallback
              src="/images/enter/change-email-icon.png"
              alt="Change email icon"
              width={80}
              height={80}
              priority
            />
          </div>

          <div className={styles.contentContainer}>
            <h3 className="aboutUsTypographyHeaderLarge noMargin">
              Change Email
            </h3>

            <input
              type="text"
              autoComplete="username"
              autoCorrect="off"
              value={currentEmailAddress}
              className={styles.emailField}
              disabled
            />

            <input
              placeholder="New email address"
              type="text"
              autoComplete="username"
              autoCorrect="off"
              onChange={(e) => {
                setNewEmailAddressText(e.target.value);
                setError(null);
              }}
              value={newEmailAddressText}
              className={styles.emailField}
            />

            {error && (
              <div
                style={{
                  boxShadow: '0 0 0 1px #e0b4b4 inset, 0 0 0 0 transparent',
                  backgroundColor: '#fff6f6',
                  color: '#9f3a38',
                  padding: '0.5em 0.75em',
                  borderRadius: '4px',
                  marginTop: '0.5em',
                  fontSize: '0.7em',
                }}
              >
                {error}
              </div>
            )}
          </div>

          <RoundedButton
            rebrandDm2025
            onClick={async () => {
              const email = newEmailAddressText.toLowerCase().trim();

              setError(null);
              setCtaLoading(true);

              try {
                // First, check that the email is valid
                if (!validEmail(email)) {
                  setError(`"${email}" is not a valid email address.`);
                  return;
                }

                // The server only lets us update email addresses if the user does not have any social logins
                if (currentUser && !currentUser.hasSocialLogin) {
                  const updatePlayerRequest: Zod.TypeOf<typeof ZPlayerUpdate> =
                    {
                      firstName: currentUser.firstName,
                      lastName: currentUser.lastName,
                      phoneNumber: currentUser.phoneNumber,
                      gender: currentUser.gender,
                      dateOfBirth: currentUser.dateOfBirth,
                      interests: currentUser.interests,
                      emailAddress: email,
                    };

                  const result = await playerApiClient.auth.updateDetails({
                    body: {
                      player: updatePlayerRequest,
                    },
                  });

                  if (result.status === HttpStatusCode.OK) {
                    displayToast({
                      title: `Successfully updated email address to ${email}`,
                      color: '#00ff00',
                      timeout: 7000,
                    });

                    await updateCurrentUser();

                    // Resend confirmation email if subscriptionId is provided
                    if (subscriptionId) {
                      const result = await tryApi(
                        subscriptionApiClient.auth.resendConfirmationEmail({
                          params: {
                            subscription_id: subscriptionId,
                          },
                          body: {},
                        }),
                      );
                      if (result.status === 'error') {
                        throw result.error;
                      }
                    }

                    onClose();
                  } else {
                    setError(
                      'message' in result.body
                        ? result.body.message
                        : 'An unknown error occurred',
                    );
                  }
                } else {
                  setError(
                    'You cannot update your email, as you are using a social login with your account',
                  );
                }
              } finally {
                setCtaLoading(false);
              }
            }}
            loading={ctaLoading}
            fullWidth
          >
            Save Changes
          </RoundedButton>

          <button
            className={`${styles.changeEmailCancelButton} aboutUsTypographyHeaderMedium`}
            onClick={onClose}
          >
            Cancel
          </button>
        </div>
      </GenericModal>
    );
  },
);

export default ChangeEmailPopup;
